import * as React from 'react';
import styled from 'styled-components';

import { CoinName } from 'app/components/coin_name';
import { CoinIcon, DummyIcon } from 'app/components/coin_icon';
import { DisplayValue } from 'app/components/display_value';
import { CoinConversions } from 'app/types/interface';
import { PriceSymbol } from 'app/types/interface';
import ArrowUp from 'app/assets/images/mini.arrow.up.svg';
import ArrowDown from 'app/assets/images/mini.arrow.down.svg';
import { colors } from 'app/styles/colors';
import { MicroStaticLoadingSpinner as Spinner } from 'app/components/loading_spinner';
import { media, NonDesktop } from 'app/utils/responsive';

const NBSP = '\xa0';

const Wrapper = styled.tr<{ disabled: boolean }>`
  ${props =>
    props.disabled
      ? `
    opacity: 0.4;
    cursor: default;
  `
      : 'cursor: pointer;'};

  ${CoinName} {
    margin-left: 8px;
  }
`;

const VerticalCenter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  svg {
    margin-left: 3px;

    /*
     * The same correction the coin icon needs below, for the same reason: Circular's
     * digits are cap height, so the price fills only the upper part of its line box and
     * a box-centred arrow reads two pixels low beside it. Measured, not guessed.
     *
     * One value rather than two here, because the price cell stays 14px at every
     * breakpoint while the coin cell changes size.
     */
    position: relative;
    top: -2px;
  }
`;

const Flex = styled.div`
  display: flex;
  align-items: center;

  /*
   * Circular seats its cap height above the middle of the em box, so an all-caps name
   * like ATOM only fills the upper part of its line box. align-items centres the boxes,
   * which leaves the icon looking about two pixels low next to the text.
   *
   * Nudge the icon down onto the text's optical centre. The offset tracks font size and
   * the coin cell is 12px on desktop but 14px once the disclosure column appears, so the
   * breakpoints need different values. Chromium snaps this offset to whole pixels, so
   * fractional values do nothing. Measured against the real font rather than guessed:
   * leaves 0.33px on desktop and 0.08px below it.
   */
  ${CoinIcon} {
    position: relative;
    top: -1px;
  }

  ${media.nonDesktop} {
    ${CoinIcon} {
      top: -2px;
    }
  }
`;

type Props = {
  count: number;
  onMouseLeave?: (event: React.MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent | React.TouchEvent) => void;
  disabled: boolean;
  highlighted: boolean;
  onClick?: (event: React.MouseEvent) => void;
  icon: string | undefined;
  amount: number;
  data: CoinConversions;
  currentPrice: number;
  coin: string;
  toSymbol: PriceSymbol;
  isLoading: boolean;
  isExpanded: boolean;
};

const circleStyle = {
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 10,
  height: 10,
  width: 10,
  padding: 2,
};
const greenCircleStyle = { ...circleStyle, borderColor: colors.green };
const redCircleStyle = { ...circleStyle, borderColor: colors.red };

class CoinRow extends React.Component<Props> {
  dragging = false;

  render() {
    const props = this.props;

    const changePlusMinus = props.data ? props.data.USD.change.percent.hour24 / 100.0 : 0;
    let coinIcon = <DummyIcon />;
    if (props.isLoading) {
      coinIcon = <Spinner />;
    } else if (props.icon) {
      coinIcon = <CoinIcon src={props.icon} />;
    }

    let disclosure = NBSP;
    if (props.highlighted && props.isExpanded) disclosure = '▼';
    else if (props.highlighted && !props.isExpanded) disclosure = '▶';

    return (
      <Wrapper
        disabled={props.disabled}
        onClick={props.onClick}
        onTouchMove={() => (this.dragging = true)}
        onTouchEnd={e => {
          if (props.onMouseEnter && !this.dragging) props.onMouseEnter(e);
          this.dragging = false;
          if (e.cancelable) e.preventDefault();
          return true;
        }}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        className={props.highlighted ? 'highlighted' : ''}
      >
        <NonDesktop>
          <td>{disclosure}</td>
        </NonDesktop>
        <td>
          <Flex>
            {coinIcon}
            <CoinName>{props.coin.slice(0, 10)}</CoinName>
          </Flex>
        </td>
        <td>{Math.round(props.count * 100) / 100}</td>
        <td>
          <div>
            <DisplayValue value={props.amount} kind={props.toSymbol} />
          </div>
        </td>
        <td>
          <VerticalCenter>
            <DisplayValue changeFlash value={props.currentPrice} change={changePlusMinus} kind={props.toSymbol} />
            {changePlusMinus >= 0 ? <ArrowUp style={greenCircleStyle} /> : <ArrowDown style={redCircleStyle} />}
          </VerticalCenter>
        </td>
      </Wrapper>
    );
  }
}

export { CoinRow };
